import React, { useCallback, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Editor from "@monaco-editor/react";
import DismissibleMessage from "./DismissibleMessage";
import { calibrationService, DEFAULT_GROUP_KEY } from "../../../services/CalibrationService";
import { makeStyles } from "@mui/styles";
import LoadingOverlay from "../../core/LoadingOverlay";
import PalmPage from "./PalmPage";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { ClientsDropdown, DatasetTypesDropdown, DatetimeDropdown, ProjectsDropdown } from "./DropdownV2";
import ConfirmModal from "./ConfirmModal";
import { addTimeoutToPromise } from "./util";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Collapse from "@mui/material/Collapse";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const SIMULATION_AREAS_DATASET_TYPE = "palm-simulation-areas";
const SIMULATION_SPANS_DATASET_TYPE = "palm-simulation-spans";
const PALM_JOBS_DATASET_TYPE = "palm-jobs";
const DEVICE_DATASET_TYPE = "device-data";
const SPAN_COEFFICIENTS_DATASET_TYPE = "span-palm-coefficients";
const WEATHER_SENSOR_COEFFICIENTS_DATASET_TYPE = "weather-sensor-palm-coefficients";
const SPAN_COEFFICIENTS_KMZ_DATASET_TYPE = "span-palm-coefficients-kmz";
const COEFFICIENTS_DATASET_TYPE = [SPAN_COEFFICIENTS_DATASET_TYPE, WEATHER_SENSOR_COEFFICIENTS_DATASET_TYPE];
const useStyles = makeStyles((theme) => ({
    formContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        width: "95%",
        margin: "0 auto",
        padding: theme.spacing(3),
    },
    selectContainer: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        width: "100%",
        marginBottom: theme.spacing(2),
    },
    tabDatasetSelect: {
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        width: "100%",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
        maxWidth: 300,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
    dropdownContainer: {
        display: "flex",
        flexDirection: "column",
        minWidth: 200,
        marginRight: theme.spacing(2),
    },
    checkboxContainer: {
        marginTop: theme.spacing(1),
    },
    buttonContainer: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        marginTop: theme.spacing(2),
    },
    metaData: {
        marginTop: theme.spacing(1),
        textAlign: "center",
        flexGrow: 1,
        fontSize: "16px",
    },
    button: {
        height: "50px",
    },
    downloadButton: {
        height: "50px",
        marginTop: theme.spacing(2),
    },
    downloadButtonContainer: {
        display: "flex",
        gap: theme.spacing(2),
        marginTop: theme.spacing(2),
    },
    modalBox: {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        backgroundColor: "#FFFFFF",
        border: "2px solid #000",
        boxShadow: theme.shadows[5],
        padding: theme.spacing(4),
        height: "70vh",
        width: "90vw",
        overflowY: "auto",
    },
    boxFullWidth: {
        width: "100vw",
        padding: theme.spacing(2),
        boxSizing: "border-box",
        overflowX: "auto",
    },
    simAreaInputContainer: {
        width: "100%",
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    checkboxListContainer: {
        width: "100%",
        maxHeight: "400px",
        overflowY: "auto",
        padding: theme.spacing(2),
        marginTop: theme.spacing(2),
        border: "1px solid #e0e0e0",
        borderRadius: theme.spacing(1),
    },
    checkboxListHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: theme.spacing(2),
    },
    selectAllButtons: {
        display: "flex",
        gap: theme.spacing(1),
    },
}));

export default function PalmCoefficients() {
    const classes = useStyles();
    const [client, setClient] = useState(null);
    const [project, setProject] = useState(null);
    const [simulationAreaVersion, setSimulationAreaVersion] = useState(null);
    const [palmJobVersion, setPalmJobVersion] = useState(null);
    const [coefficientType, setCoefficientType] = useState(null);
    const [coefficientVersion, setCoefficientVersion] = useState(null);
    const [kmzVersion, setKmzVersion] = useState(null); // auto-matched
    const [kmzVersionOverride, setKmzVersionOverride] = useState(null);

    const [simulationAreaDataset, setSimulationAreaDataset] = useState(null);
    const [palmJobsDataset, setPalmJobsDataset] = useState(null);
    // Note that simulationSpanDataset is only needed for PALM map tab. The
    // versionDttm of this dataset is the same as the simulationAreaVersion,
    // not the simulationSpanVersion used in the extraction tab.
    const [simulationSpanDataset, setSimulationSpanDataset] = useState(null);
    const [coefficientsDataset, setCoefficientsDataset] = useState(null);
    const [validationDataset, setValidationDataset] = useState(null);
    const [terrainVizData, setTerrainVizData] = useState(null);

    const [potentialSimulationAreasToRun, setPotentialSimulationAreasToRun] = useState([]);
    const [simulationAreasToRun, setSimulationAreasToRun] = useState([]);
    const [simAreaInputValue, setSimAreaInputValue] = useState("");
    const [selectedAreasExpanded, setSelectedAreasExpanded] = useState(false);

    const [simulationSpanVersion, setSimulationSpanVersion] = useState(null);
    const [deviceDatasetVersion, setDeviceDatasetVersion] = useState(null);

    const [extractingCoefficients, setExtractingCoefficients] = useState(false);
    const [initiatingPalm, setInitiatingPalm] = useState(false);
    const [loadingDataset, setLoadingDataset] = useState(false);
    const [downloadingKmz, setDownloadingKmz] = useState(false);
    const [uploadingToDatabase, setUploadingToDatabase] = useState(false);
    const [tearingDownFromDatabase, setTearingDownFromDatabase] = useState(false);

    const [viewCoefficientsModal, setViewCoefficientsModal] = useState(false);
    const [databaseUpload, setDatabaseUpload] = useState(false);
    const [databaseTeardown, setDatabaseTeardown] = useState(false);
    const [checkingDatabaseStatus, setCheckingDatabaseStatus] = useState(false);
    const [validationFailed, setValidationFailed] = useState(false);

    const [palmRunsActive, setPalmRunsActive] = useState(false);
    const [checkingPalmRuns, setCheckingPalmRuns] = useState(false);

    const [selectedTab, setSelectedTab] = useState(0);
    const [statusMessage, setStatusMessage] = useState(null);
    const [error, setError] = useState(null);

    // To add new tabs, simply add a new entry to the TABS object
    // Order of tabs in the TABS object is maintained in the UI
    // Tab name shown in UI is the value of each entry
    const TABS = {
        GENERATE_PALM_INPUTS: "Generate Palm Inputs",
        RUN_PALM: "Run PALM on Existing Inputs",
        EXTRACT_COEFFICIENTS: "Extract Coefficients",
        VIEW_COEFFICIENTS: "View & Manage Coefficients",
    };
    const TAB_LIST = Object.values(TABS);

    const clearMessages = useCallback(() => {
        setStatusMessage(null);
        setError(null);
    }, []);

    // Set error message (persists until cleared by user action or navigation)
    const setErrorWithTimeout = useCallback((errorMsg) => {
        setError(errorMsg);
    }, []);

    // Set status message (persists until cleared by user action or navigation)
    const setStatusWithTimeout = useCallback((status) => {
        setStatusMessage(status);
    }, []);

    const toggleViewCoefficientsModal = () => setViewCoefficientsModal(!viewCoefficientsModal);

    const handleTabChange = (event, newValue) => {
        clearMessages();
        setSimulationAreaVersion(null);
        setSimulationAreaDataset(null);
        setSimulationSpanDataset(null);
        setPalmJobVersion(null);
        setPalmJobsDataset(null);
        setPotentialSimulationAreasToRun([]);
        setSimulationAreasToRun([]);
        setSimAreaInputValue("");
        setSelectedAreasExpanded(false);
        setCoefficientType(null);
        setCoefficientVersion(null);
        setSimulationSpanVersion(null);
        setDeviceDatasetVersion(null);
        setTerrainVizData(null);
        setSelectedTab(newValue);
    };

    const fetchDataset = async (datasetType, versionDttm, setter, data_only = true) => {
        setLoadingDataset(true);
        try {
            const dataset = await calibrationService.getExactDataset(
                client,
                project,
                DEFAULT_GROUP_KEY,
                datasetType,
                versionDttm
            );
            if (data_only) {
                setter(dataset.data);
            } else {
                setter(dataset);
            }
        } catch (error) {
            const errorMessage =
                error?.response?.data?.error || error?.response?.data?.message || error?.message || error;
            console.error(
                `There was an error initializing the ${datasetType} dataset from this file S3, ${errorMessage}`
            );
            setErrorWithTimeout(`Failed to get the dataset chosen: ${errorMessage}`);
        } finally {
            setLoadingDataset(false);
        }
    };

    const fetchSimulationSpansDataset = async () => {
        setLoadingDataset(true);
        console.log("Trying to load simulation spans dataset...");
        try {
            const dataset = await calibrationService.getExactDataset(
                client,
                project,
                DEFAULT_GROUP_KEY,
                SIMULATION_SPANS_DATASET_TYPE,
                simulationAreaVersion
            );
            setSimulationSpanDataset(dataset.data);
        } catch (error) {
            if (error.response?.status === 404) {
                setErrorWithTimeout(
                    `Simulation spans dataset not found. Contact OBS SWEs to regenerate simulation areas for this project.`
                );
                setSimulationSpanDataset(null);
                console.log("Failed to load simulation spans dataset: 404 not found.");
            }
        } finally {
            setLoadingDataset(false);
        }
    };

    useEffect(() => {
        if (
            (selectedTab === TAB_LIST.indexOf(TABS.GENERATE_PALM_INPUTS) ||
                selectedTab === TAB_LIST.indexOf(TABS.VIEW_COEFFICIENTS)) &&
            simulationAreaVersion
        ) {
            fetchDataset(SIMULATION_AREAS_DATASET_TYPE, simulationAreaVersion, setSimulationAreaDataset);
            fetchSimulationSpansDataset();
        }
    }, [selectedTab, simulationAreaVersion]);

    useEffect(() => {
        if (selectedTab === TAB_LIST.indexOf(TABS.RUN_PALM) && palmJobVersion) {
            fetchDataset(PALM_JOBS_DATASET_TYPE, palmJobVersion, setPalmJobsDataset, false);
        }
    }, [selectedTab, palmJobVersion]);

    useEffect(() => {
        if (selectedTab === TAB_LIST.indexOf(TABS.RUN_PALM)) {
            setPotentialSimulationAreasToRun(Object.keys(palmJobsDataset?.data.simulations || []));
        }
    }, [selectedTab, palmJobsDataset]);

    useEffect(() => {
        if (coefficientVersion) {
            fetchDataset(coefficientType, coefficientVersion, setCoefficientsDataset);
        }
    }, [coefficientVersion]);

    const fetchTerrainViz = async () => {
        if (!client || !project || !coefficientType || !coefficientVersion) {
            setTerrainVizData(null);
            return;
        }
        try {
            const data = await calibrationService.getPalmTerrainVisualization(
                client,
                project,
                coefficientType,
                coefficientVersion
            );
            setTerrainVizData(data.data ?? null);
        } catch {
            try {
                const dataset = await calibrationService.getLatestDatasetViaSignedUrl(
                    client,
                    project,
                    DEFAULT_GROUP_KEY,
                    "palm-terrain-visualization",
                    false
                );
                if (!dataset) {
                    setTerrainVizData(null);
                    setErrorWithTimeout("No terrain visualization found for this project, contact OBS to generate one");
                    return;
                }
                setStatusWithTimeout(
                    "No terrain visualization found for the existing coefficient dataset, displaying the most recent"
                );
                setTerrainVizData(dataset?.data ?? null);
            } catch {
                setTerrainVizData(null);
                setErrorWithTimeout("No terrain visualization found for this project, contact OBS to generate one");
            }
        }
    };

    useEffect(() => {
        fetchTerrainViz();
    }, [client, project, coefficientType, coefficientVersion]);

    const handleRunPalmInputGeneration = useCallback(async () => {
        if (simulationAreaVersion) {
            try {
                setInitiatingPalm(true);
                await calibrationService.generatePalmInputs(client, project, simulationAreaVersion);
                setStatusWithTimeout(
                    "Attempting to run PALM... Please go to the Progress Viewer tool under the 'GeneratePalmInputs' process type to watch your process!",
                    10000
                );
            } catch (error) {
                const errorMessage =
                    error?.response?.data?.error || error?.response?.data?.message || error?.message || error;
                console.error(`There was an error initiating PALM input generation: ${errorMessage}`);
                setErrorWithTimeout(`There was an error initiating PALM input generation: ${errorMessage}`);
            } finally {
                setInitiatingPalm(false);
            }
        }
    }, [client, project, simulationAreaVersion]);

    const handleRunPalm = useCallback(async () => {
        if (palmJobVersion && simulationAreasToRun.length > 0) {
            try {
                setInitiatingPalm(true);
                await calibrationService.enqueuePalmJob(client, project, palmJobVersion, simulationAreasToRun);
                // Clear inputs after successful submission
                setPalmJobVersion(null);
                setSimulationAreasToRun([]);
                setSimAreaInputValue("");
                setSelectedAreasExpanded(false);

                setStatusWithTimeout(
                    "Attempting to run PALM... Please go to the Progress Viewer tool under the 'Palm' process type to watch your process!",
                    10000
                );
            } catch (error) {
                const errorMessage =
                    error?.response?.data?.error || error?.response?.data?.message || error?.message || error;
                console.error(`There was an error initiating PALM: ${errorMessage}`);
                setErrorWithTimeout(`There was an error initiating PALM: ${errorMessage}`);
            } finally {
                setInitiatingPalm(false);
            }
        }
    }, [client, project, palmJobVersion, simulationAreasToRun]);

    const handleDownloadJson = () => {
        if (coefficientsDataset) {
            const blob = new Blob([JSON.stringify(coefficientsDataset, null, 2)], { type: "application/json" });
            const href = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = href;
            link.download = `${client}_${project}_coefficients` + ".json";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            setErrorWithTimeout("No coefficients dataset to download");
        }
    };

    const handleDownloadKmz = async () => {
        setDownloadingKmz(true);
        try {
            const effectiveVersion = kmzVersionOverride || kmzVersion;
            const data = await calibrationService.getExactDataset(
                client,
                project,
                DEFAULT_GROUP_KEY,
                SPAN_COEFFICIENTS_KMZ_DATASET_TYPE,
                effectiveVersion,
                true
            );
            const blob = new Blob([data], { type: "application/vnd.google-earth.kmz" });
            const href = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = href;
            link.download = `${client}_${project}_span_coefficients.kmz`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            const errorMessage =
                error?.response?.data?.error || error?.response?.data?.message || error?.message || error;
            console.error(`There was an error downloading KMZ: ${errorMessage}`);
            setErrorWithTimeout(`Failed to download KMZ: ${errorMessage}`);
        } finally {
            setDownloadingKmz(false);
        }
    };

    const extractCoefficients = useCallback(async () => {
        try {
            setExtractingCoefficients(true);
            await calibrationService.extractCoefficients(client, project, simulationSpanVersion, deviceDatasetVersion);
            setStatusWithTimeout(
                "Attempted to start the coefficient extraction process... Please go to the Progress Viewer tool to watch your process!"
            );
        } catch (error) {
            const errorMessage =
                error?.response?.data?.error || error?.response?.data?.message || error?.message || error;
            console.error(`There was an error enqueuing extraction jobs: ${errorMessage}`);
            setErrorWithTimeout(`Failed to enqueue coefficient extraction jobs: ${errorMessage}`);
        } finally {
            setExtractingCoefficients(false);
            setSimulationSpanVersion(null);
            setDeviceDatasetVersion(null);
        }
    }, [client, project, simulationSpanVersion, deviceDatasetVersion]);

    const extractCoefficientsConfirmMessage = useCallback(() => {
        var text = `Are you sure you want to extract the following coefficients for ${client}/${project}: `;
        if (simulationSpanVersion && deviceDatasetVersion) {
            text = `${text}spans, structures, weather sensors`;
        } else if (simulationSpanVersion) {
            text = `${text}spans, structures`;
        } else if (deviceDatasetVersion) {
            text = `${text}weather sensors`;
        }
        return text;
    }, [client, project, simulationSpanVersion, deviceDatasetVersion]);

    const checkCoefficientDatabaseStatus = useCallback(async () => {
        setStatusMessage(null);
        setCheckingDatabaseStatus(true);
        try {
            const response = await calibrationService.checkCoefficientDatabaseStatus(
                client,
                project,
                coefficientType,
                coefficientVersion
            );
            if (response.status === "Upload ready") {
                setDatabaseUpload(true);
                setDatabaseTeardown(false);
                setValidationFailed(false);
            } else if (response.status === "Teardown ready") {
                setDatabaseUpload(false);
                setDatabaseTeardown(true);
                setValidationFailed(false);
            } else if (response.status === "Validation failed") {
                setErrorWithTimeout("Coefficient validation failed, check below for details. \nUpload is enabled but manual QA is recommended.");
                setDatabaseUpload(true);
                setDatabaseTeardown(false);
                setValidationFailed(true);
            } else {
                setErrorWithTimeout(response.message + " Database upload/teardown is disabled.");
                setDatabaseUpload(false);
                setDatabaseTeardown(false);
                setValidationFailed(false);
            }
        } catch (error) {
            const errorMessage =
                error?.response?.data?.error || error?.response?.data?.message || error?.message || error;
            setErrorWithTimeout(`Failed to check database status of CFD coefficients: ${errorMessage}`);
        } finally {
            setCheckingDatabaseStatus(false);
        }
    }, [client, project, coefficientType, coefficientVersion]);

    useEffect(() => {
        if (
            selectedTab === TAB_LIST.indexOf(TABS.VIEW_COEFFICIENTS) &&
            client &&
            project &&
            coefficientType &&
            coefficientVersion
        ) {
            checkCoefficientDatabaseStatus(client, project, coefficientType, coefficientVersion);
        }
    }, [client, project, coefficientType, coefficientVersion]);

    useEffect(() => {
        const onGenerateTab =
            selectedTab === TAB_LIST.indexOf(TABS.GENERATE_PALM_INPUTS) && simulationAreaVersion;
        const onRunPalmTab = selectedTab === TAB_LIST.indexOf(TABS.RUN_PALM) && palmJobVersion;
        if (client && project && (onGenerateTab || onRunPalmTab)) {
            setCheckingPalmRuns(true);
            setPalmRunsActive(false);
            calibrationService
                .checkNoActivePalmRuns(client, project)
                .then((response) => {
                    const active = response?.data?.data === "Active PALM runs found";
                    setPalmRunsActive(active);
                    if (active) {
                        setErrorWithTimeout(
                            `Active PALM runs detected for ${client}/${project}. Please wait for them to complete before starting a new run.`
                        );
                    }
                })
                .catch((error) => {
                    setPalmRunsActive(true);
                    const errorMessage =
                        error?.response?.data?.error || error?.response?.data?.message || error?.message || error;
                    setErrorWithTimeout(`Failed to check for active PALM runs: ${errorMessage}`);
                })
                .finally(() => {
                    setCheckingPalmRuns(false);
                });
        }
    }, [client, project, selectedTab, simulationAreaVersion, palmJobVersion]);

    const handleUploadTeardown = useCallback(
        async (operation) => {
            const operation_capitalized = operation.charAt(0).toUpperCase() + operation.slice(1);
            const setLoading = operation === "upload" ? setUploadingToDatabase : setTearingDownFromDatabase;
            setLoading(true);
            try {
                let response;
                if (coefficientType == SPAN_COEFFICIENTS_DATASET_TYPE) {
                    response = await addTimeoutToPromise(
                        calibrationService.spanCfdCoefficientsTransaction(
                            client,
                            project,
                            coefficientVersion,
                            operation
                        )
                    );
                } else if (coefficientType == WEATHER_SENSOR_COEFFICIENTS_DATASET_TYPE) {
                    response = await addTimeoutToPromise(
                        calibrationService.weatherSensorCfdCoefficientsTransaction(
                            client,
                            project,
                            coefficientVersion,
                            operation
                        )
                    );
                }
                if (response === "timeout") {
                    setStatusWithTimeout(
                        `${operation_capitalized} is still running... Check back within a few minutes.`
                    );
                    return;
                }
                setStatusWithTimeout(`${operation_capitalized} successful!`);
                // Refresh the database status after successful operation
                await checkCoefficientDatabaseStatus();
            } catch (error) {
                // Extract the most useful error message
                const errorMessage =
                    typeof error === "string" ? error : error?.response?.data?.error || error?.message || String(error);
                setErrorWithTimeout(`${operation_capitalized} failed: ${errorMessage}`);
            } finally {
                setLoading(false);
            }
        },
        [client, project, coefficientType, coefficientVersion, checkCoefficientDatabaseStatus]
    );

    // Resetting state when client, project, etc. changes
    useEffect(() => {
        clearMessages();
        setProject(null);
    }, [client]);

    useEffect(() => {
        clearMessages();
        setSimulationAreaVersion(null);
        setCoefficientType(null);
    }, [project]);

    useEffect(() => {
        clearMessages();
        setCoefficientVersion(null);
        setKmzVersion(null);
        setKmzVersionOverride(null);
    }, [coefficientType]);

    useEffect(() => {
        if (!coefficientVersion || coefficientType !== SPAN_COEFFICIENTS_DATASET_TYPE) {
            setKmzVersion(null);
            return;
        }
        const coeffTime = new Date(coefficientVersion).getTime();
        calibrationService
            .getFileNames(client, project, DEFAULT_GROUP_KEY, SPAN_COEFFICIENTS_KMZ_DATASET_TYPE)
            .then((response) => {
                const versions = response?.data || [];
                // Find the closest KMZ version within 5 minutes of the coefficient version
                const MAX_DIFF_MS = 5 * 60 * 1000;
                let closest = null;
                let closestDiff = Infinity;
                for (const v of versions) {
                    const diff = Math.abs(new Date(v).getTime() - coeffTime);
                    if (diff < closestDiff && diff <= MAX_DIFF_MS) {
                        closest = v;
                        closestDiff = diff;
                    }
                }
                setKmzVersion(closest);
            })
            .catch(() => {
                setKmzVersion(null);
            });
    }, [coefficientVersion, coefficientType, client, project]);

    // Fetch the coefficient-validation dataset matching the selected coefficient version
    useEffect(() => {
        if (!coefficientVersion || coefficientType !== SPAN_COEFFICIENTS_DATASET_TYPE) {
            setValidationDataset(null);
            return;
        }
        const COEFFICIENT_VALIDATION_DATASET_TYPE = "coefficient-validation";
        const VALIDATION_GROUP_KEY = "span";
        const coeffTimeSec = Math.floor(new Date(coefficientVersion).getTime() / 1000);
        calibrationService
            .getFileNames(client, project, VALIDATION_GROUP_KEY, COEFFICIENT_VALIDATION_DATASET_TYPE)
            .then(async (response) => {
                const versions = response?.data || [];
                // Try each version (most recent first) to find one whose input matches the coefficient version
                const sortedVersions = [...versions].sort((a, b) => new Date(b) - new Date(a));
                for (const v of sortedVersions) {
                    try {
                        const dataset = await calibrationService.getExactDataset(
                            client,
                            project,
                            VALIDATION_GROUP_KEY,
                            COEFFICIENT_VALIDATION_DATASET_TYPE,
                            v
                        );
                        const inputVersion = dataset?.meta?.input_datasets?.[0]?.version_dttm;
                        // Compare as timestamps to handle format differences (e.g. timezone offsets)
                        // Compare rounded to seconds to handle millisecond precision differences
                        const inputTime = Math.floor(new Date(inputVersion).getTime() / 1000);
                        if (inputVersion && inputTime === coeffTimeSec) {
                            setValidationDataset(dataset);
                            return;
                        }
                    } catch {
                        // Skip versions that fail to load
                    }
                }
                setValidationDataset(null);
            })
            .catch(() => {
                setValidationDataset(null);
            });
    }, [coefficientVersion, coefficientType, client, project]);

    useEffect(() => {
        clearMessages();
        setSimulationAreaDataset(null);
        setSimulationSpanDataset(null);
    }, [simulationAreaVersion]);

    useEffect(() => {
        clearMessages();
        setPalmJobsDataset(null);
        setPotentialSimulationAreasToRun([]);
        setSimulationAreasToRun([]);
    }, [palmJobVersion]);

    useEffect(() => {
        clearMessages();
        setCoefficientsDataset(null);
        setValidationDataset(null);
    }, [coefficientVersion, coefficientVersion]);

    useEffect(() => {
        clearMessages();
        setDatabaseUpload(false);
        setDatabaseTeardown(false);
        setValidationFailed(false);
    }, [coefficientVersion]);

    const handleAddSimArea = useCallback(() => {
        const trimmedId = simAreaInputValue.trim();
        if (trimmedId.length > 0 && !simulationAreasToRun.includes(trimmedId)) {
            if (potentialSimulationAreasToRun.includes(trimmedId)) {
                setSimulationAreasToRun((prev) => [...prev, trimmedId]);
                setSimAreaInputValue("");
            } else {
                setErrorWithTimeout(`Simulation area "${trimmedId}" does not exist in the palm jobs dataset`);
            }
        }
    }, [simAreaInputValue, simulationAreasToRun, potentialSimulationAreasToRun, setErrorWithTimeout]);

    const handleSimAreaInputKeyDown = useCallback(
        (event) => {
            if (event.key === "Enter") {
                event.preventDefault();
                handleAddSimArea();
            }
        },
        [handleAddSimArea]
    );

    const handleToggleSimArea = useCallback((simAreaId) => {
        setSimulationAreasToRun((prev) => {
            if (prev.includes(simAreaId)) {
                return prev.filter((id) => id !== simAreaId);
            } else {
                return [...prev, simAreaId];
            }
        });
    }, []);

    const handleSelectAll = useCallback(() => {
        setSimulationAreasToRun([...potentialSimulationAreasToRun]);
    }, [potentialSimulationAreasToRun]);

    const handleDeselectAll = useCallback(() => {
        setSimulationAreasToRun([]);
    }, []);

    return (
        <div id="form-container" className={classes.formContainer}>
            <h1 style={{ textAlign: "center" }}>PALM</h1>
            <div className={classes.selectContainer}>
                <ClientsDropdown value={client} setValue={setClient} styleClass={{ flex: 1, mx: 2, px: 0 }} />
                <ProjectsDropdown
                    clientName={client}
                    value={project}
                    setValue={setProject}
                    styleClass={{ flex: 1, mx: 2, px: 0 }}
                />
            </div>

            <DismissibleMessage message={error} type="error" onDismiss={() => setError(null)} />
            <DismissibleMessage message={statusMessage} type="success" onDismiss={() => setStatusMessage(null)} />
            <LoadingOverlay isLoading={extractingCoefficients} message="Extracting coefficients..." />
            <LoadingOverlay isLoading={initiatingPalm} message="Kicking off PALM..." />
            <LoadingOverlay isLoading={uploadingToDatabase} message="Uploading coefficients to database..." />
            <LoadingOverlay isLoading={tearingDownFromDatabase} message="Tearing down coefficients from database..." />

            <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                centered
                variant="scrollable"
                scrollButtons="auto"
            >
                {TAB_LIST.map((label, i) => (
                    <Tab key={i} label={label} />
                ))}
            </Tabs>

            {selectedTab === 0 && (
                <>
                    <div className={classes.tabDatasetSelect}>
                        <DatetimeDropdown
                            clientName={client}
                            projectName={project}
                            datasetType={SIMULATION_AREAS_DATASET_TYPE}
                            group={DEFAULT_GROUP_KEY}
                            value={simulationAreaVersion}
                            setValue={setSimulationAreaVersion}
                            name="Simulation Areas Version"
                            styleClass={{ flex: 1, mx: 2, px: 0 }}
                        />
                    </div>
                    {simulationAreaDataset && simulationSpanDataset && (
                        <>
                            {/* Button to run PALM */}
                            <div className={classes.downloadButtonContainer}>
                                <ConfirmModal
                                    enableButton={simulationAreaVersion && !palmRunsActive && !checkingPalmRuns}
                                    operation={handleRunPalmInputGeneration}
                                    buttonText={checkingPalmRuns ? "Checking for active runs..." : palmRunsActive ? "Active PALM runs detected" : "Generate PALM Inputs"}
                                    modalTitle={`Confirm run PALM from input generation`}
                                    modalMessage={`Are you sure you want to run PALM starting from input generation?`}
                                    modalNewLineMessage={`This will run all simulation areas (${
                                        simulationAreaDataset?.simulation_areas?.length || 0
                                    }) at an estimated cost of $${(
                                        (simulationAreaDataset?.simulation_areas?.length || 0) *
                                        (40 / 60) *
                                        4 *
                                        2.176
                                    ).toFixed(2)}.`}
                                />
                            </div>
                            <PalmPage
                                type="cad"
                                simulationAreas={simulationAreaDataset}
                                simulationSpans={simulationSpanDataset}
                            />
                        </>
                    )}
                </>
            )}
            {selectedTab === 1 && (
                <>
                    <div className={classes.tabDatasetSelect}>
                        <DatetimeDropdown
                            clientName={client}
                            projectName={project}
                            datasetType={PALM_JOBS_DATASET_TYPE}
                            group={DEFAULT_GROUP_KEY}
                            value={palmJobVersion}
                            setValue={setPalmJobVersion}
                            name="Palm Job Version"
                            styleClass={{ flex: 1, mx: 2, px: 0 }}
                        />
                    </div>
                    {potentialSimulationAreasToRun.length > 0 && (
                        <>
                            <div className={classes.downloadButtonContainer}>
                                {/* Button to run PALM */}
                                <ConfirmModal
                                    enableButton={
                                        simulationAreasToRun.length > 0 && !palmRunsActive && !checkingPalmRuns
                                    }
                                    operation={handleRunPalm}
                                    buttonText={
                                        checkingPalmRuns
                                            ? "Checking for active runs..."
                                            : palmRunsActive
                                              ? "Active PALM runs detected"
                                              : "Run PALM"
                                    }
                                    modalTitle={`Confirm run PALM from existing inputs`}
                                    modalMessage={`Are you sure you want to run PALM starting from the existing inputs?`}
                                    modalNewLineMessage={`This will run the selected simulation areas (${
                                        simulationAreasToRun.length
                                    }) at an estimated cost of $${(
                                        simulationAreasToRun.length *
                                        (40 / 60) *
                                        4 *
                                        2.176
                                    ).toFixed(2)}.`}
                                />
                            </div>

                            {/* Selected count badge - expandable */}
                            <Box
                                style={{
                                    marginTop: "16px",
                                    marginBottom: "8px",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                }}
                            >
                                <Box
                                    onClick={() => setSelectedAreasExpanded(!selectedAreasExpanded)}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        cursor: simulationAreasToRun.length > 0 ? "pointer" : "default",
                                        userSelect: "none",
                                    }}
                                >
                                    <Typography variant="subtitle1" color="primary" style={{ fontWeight: 600 }}>
                                        {simulationAreasToRun.length} simulation area
                                        {simulationAreasToRun.length !== 1 ? "s" : ""} selected
                                    </Typography>
                                    {simulationAreasToRun.length > 0 &&
                                        (selectedAreasExpanded ? (
                                            <ExpandLessIcon color="primary" />
                                        ) : (
                                            <ExpandMoreIcon color="primary" />
                                        ))}
                                </Box>
                                <Collapse in={selectedAreasExpanded && simulationAreasToRun.length > 0}>
                                    <Box style={{ marginTop: "8px", paddingLeft: "16px" }}>
                                        <ul style={{ margin: 0, paddingLeft: "20px" }}>
                                            {simulationAreasToRun.map((id) => (
                                                <li key={id}>
                                                    <Typography variant="body2">{id}</Typography>
                                                </li>
                                            ))}
                                        </ul>
                                    </Box>
                                </Collapse>
                            </Box>

                            {/* Input to add sim area IDs one at a time */}
                            <Box className={classes.simAreaInputContainer} style={{ display: "flex", gap: "16px" }}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    label="Simulation Area ID"
                                    value={simAreaInputValue}
                                    onChange={(e) => setSimAreaInputValue(e.target.value)}
                                    onKeyDown={handleSimAreaInputKeyDown}
                                    helperText="Enter a simulation area ID to run PALM on that area"
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleAddSimArea}
                                    style={{ height: "56px" }}
                                >
                                    Add
                                </Button>
                            </Box>

                            {/* Multi-Select Checkbox List */}
                            {potentialSimulationAreasToRun.length > 0 && (
                                <Paper className={classes.checkboxListContainer}>
                                    <div className={classes.checkboxListHeader}>
                                        <Typography variant="h6">
                                            Available Simulation Areas ({potentialSimulationAreasToRun.length})
                                        </Typography>
                                        <div className={classes.selectAllButtons}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={handleSelectAll}
                                                disabled={
                                                    simulationAreasToRun.length === potentialSimulationAreasToRun.length
                                                }
                                            >
                                                Select All
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={handleDeselectAll}
                                                disabled={simulationAreasToRun.length === 0}
                                            >
                                                Deselect All
                                            </Button>
                                        </div>
                                    </div>
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                                            gap: "8px",
                                        }}
                                    >
                                        {potentialSimulationAreasToRun.map((simAreaId) => (
                                            <FormControlLabel
                                                key={simAreaId}
                                                control={
                                                    <Checkbox
                                                        checked={simulationAreasToRun.includes(simAreaId)}
                                                        onChange={() => handleToggleSimArea(simAreaId)}
                                                        color="primary"
                                                    />
                                                }
                                                label={simAreaId}
                                            />
                                        ))}
                                    </div>
                                </Paper>
                            )}
                        </>
                    )}
                </>
            )}
            {selectedTab === 2 && (
                <>
                    <div className={classes.tabDatasetSelect}>
                        <DatetimeDropdown
                            clientName={client}
                            projectName={project}
                            datasetType={SIMULATION_SPANS_DATASET_TYPE}
                            group={DEFAULT_GROUP_KEY}
                            value={simulationSpanVersion}
                            setValue={setSimulationSpanVersion}
                            name="Span Dataset Version"
                            styleClass={{ flex: 1, mx: 2, px: 0 }}
                        />
                        <DatetimeDropdown
                            clientName={client}
                            projectName={project}
                            datasetType={DEVICE_DATASET_TYPE}
                            group={DEFAULT_GROUP_KEY}
                            value={deviceDatasetVersion}
                            setValue={setDeviceDatasetVersion}
                            name="Device Dataset Version"
                            styleClass={{ flex: 1, mx: 2, px: 0 }}
                        />
                    </div>
                    <ConfirmModal
                        enableButton={simulationSpanVersion || deviceDatasetVersion}
                        operation={extractCoefficients}
                        buttonText="Extract CFD Coefficients"
                        modalTitle={`Confirm extract coefficients`}
                        modalMessage={extractCoefficientsConfirmMessage()}
                    />
                </>
            )}
            {selectedTab === 3 && (
                <>
                    <div className={classes.tabDatasetSelect}>
                        <DatetimeDropdown
                            clientName={client}
                            projectName={project}
                            datasetType={SIMULATION_AREAS_DATASET_TYPE}
                            group={DEFAULT_GROUP_KEY}
                            value={simulationAreaVersion}
                            setValue={setSimulationAreaVersion}
                            name="Simulation Areas Version"
                            styleClass={{ flex: 1, mx: 2, px: 0 }}
                        />
                        <DatasetTypesDropdown
                            clientName={client}
                            projectName={project}
                            value={coefficientType}
                            allowedDatasetTypes={COEFFICIENTS_DATASET_TYPE}
                            setValue={setCoefficientType}
                            styleClass={{ flex: 1, mx: 2, px: 0 }}
                        />
                        <DatetimeDropdown
                            clientName={client}
                            projectName={project}
                            datasetType={coefficientType}
                            group={DEFAULT_GROUP_KEY}
                            value={coefficientVersion}
                            setValue={setCoefficientVersion}
                            name="PALM Coefficient Version"
                            styleClass={{ flex: 1, mx: 2, px: 0 }}
                        />
                        {coefficientType === SPAN_COEFFICIENTS_DATASET_TYPE && (
                            <DatetimeDropdown
                                clientName={client}
                                projectName={project}
                                datasetType={SPAN_COEFFICIENTS_KMZ_DATASET_TYPE}
                                group={DEFAULT_GROUP_KEY}
                                value={kmzVersionOverride}
                                setValue={setKmzVersionOverride}
                                name="KMZ Version Override"
                                styleClass={{ flex: 1, mx: 2, px: 0 }}
                            />
                        )}
                    </div>
                    <div className={classes.buttonContainer}>
                        <Button
                            variant="contained"
                            color="secondary"
                            className={classes.button}
                            onClick={toggleViewCoefficientsModal}
                            disabled={!coefficientsDataset || loadingDataset}
                        >
                            View Coefficients
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            className={classes.button}
                            onClick={handleDownloadJson}
                            disabled={!coefficientsDataset || loadingDataset}
                        >
                            Download Coefficients
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            className={classes.button}
                            onClick={handleDownloadKmz}
                            disabled={
                                !(kmzVersionOverride || kmzVersion) ||
                                loadingDataset ||
                                downloadingKmz ||
                                coefficientType !== SPAN_COEFFICIENTS_DATASET_TYPE
                            }
                        >
                            Download KMZ
                        </Button>
                        {(loadingDataset || downloadingKmz) && <CircularProgress size={24} sx={{ ml: 2 }} />}
                        <Modal open={viewCoefficientsModal} onClose={toggleViewCoefficientsModal}>
                            <Box className={classes.modalBox}>
                                <Editor
                                    height="70vh"
                                    language="json"
                                    value={JSON.stringify(coefficientsDataset, null, 2)}
                                    options={{ readOnly: true }}
                                />
                            </Box>
                        </Modal>
                    </div>
                    <div className={classes.buttonContainer}>
                        <ConfirmModal
                            enableButton={coefficientVersion && databaseUpload}
                            operation={handleUploadTeardown.bind(null, "upload")}
                            buttonText="Upload to DB"
                            buttonColor={validationFailed ? "warning" : "primary"}
                            modalTitle={`Confirm upload ${coefficientType}`}
                            modalMessage={`Are you sure you want to upload ${coefficientType} to the 'telemetry_schema' DB for ${client}/${project}`}
                            warningMessage={
                                validationFailed
                                    ? "Validation failed — manual QA is strongly recommended before uploading."
                                    : undefined
                            }
                        />
                        <ConfirmModal
                            enableButton={coefficientVersion && databaseTeardown}
                            operation={handleUploadTeardown.bind(null, "teardown")}
                            buttonText="Teardown from DB"
                            modalTitle={`Confirm teardown ${coefficientType}`}
                            modalMessage={`Are you sure you want to teardown ${coefficientType} from the 'telemetry_schema' DB for ${client}/${project}`}
                        />
                        {checkingDatabaseStatus && <CircularProgress size={24} />}
                    </div>
                    {validationDataset?.data && (
                        <Paper variant="outlined" sx={{ p: 2, mt: 2, border: "1px solid #ccc" }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                                Coefficient Validation Results
                            </Typography>
                            {validationDataset.data.high_coefficients?.length > 0 && (
                                <Box sx={{ mb: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "red" }}>
                                        High Coefficients ({validationDataset.data.high_coefficients.length}):
                                    </Typography>
                                    <Typography variant="body2">
                                        {validationDataset.data.high_coefficients.join(", ")}
                                    </Typography>
                                </Box>
                            )}
                            {validationDataset.data.low_coefficients?.length > 0 && (
                                <Box sx={{ mb: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "red" }}>
                                        Low Coefficients ({validationDataset.data.low_coefficients.length}):
                                    </Typography>
                                    <Typography variant="body2">
                                        {validationDataset.data.low_coefficients.join(", ")}
                                    </Typography>
                                </Box>
                            )}
                            {validationDataset.data.high_angle_differences?.length > 0 && (
                                <Box sx={{ mb: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "red" }}>
                                        High Angle Differences ({validationDataset.data.high_angle_differences.length}):
                                    </Typography>
                                    <Typography variant="body2">
                                        {validationDataset.data.high_angle_differences.join(", ")}
                                    </Typography>
                                </Box>
                            )}
                            {!validationDataset.data.high_coefficients?.length &&
                                !validationDataset.data.low_coefficients?.length &&
                                !validationDataset.data.high_angle_differences?.length && (
                                    <Typography variant="body2" sx={{ color: "green" }}>
                                        All spans passed validation.
                                    </Typography>
                                )}
                        </Paper>
                    )}
                    {simulationAreaDataset && simulationSpanDataset && coefficientType && coefficientsDataset && (
                        <>
                            {coefficientType === SPAN_COEFFICIENTS_DATASET_TYPE ? (
                                <PalmPage
                                    type="span"
                                    simulationAreas={simulationAreaDataset}
                                    simulationSpans={simulationSpanDataset}
                                    coefficients={coefficientsDataset}
                                    failedSpans={validationDataset?.data}
                                    terrainData={terrainVizData?.sim_area_visualizations}
                                />
                            ) : (
                                <PalmPage
                                    type="weatherSensor"
                                    simulationAreas={simulationAreaDataset}
                                    simulationSpans={simulationSpanDataset}
                                    coefficients={coefficientsDataset}
                                    terrainData={terrainVizData?.sim_area_visualizations}
                                />
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
}
